var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { forwardRef, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Tooltip } from '@arco-design/web-react';
import { useConfigContext } from '@m4b-design/config-provider';
import { IconSuccess, IconError, IconWarning, IconClose, IconInfoBlue, } from '@arco-design/iconbox-react-m4b-next';
import cx from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { ArcoIconWrapper } from '@m4b-design/utils';
import { useControlled } from './hooks/use-controlled';
import './style/alert.less';
import { useI18nCopy } from './hooks/useI18nCopy';
import { MoreIcon } from './components/MoreIcon';
import { CollapseIcon } from './components/CollapseIcon';
var AlertRender = function (props, ref) {
    var _a, _b;
    var _c;
    var style = props.style, className = props.className, wrapperClassName = props.wrapperClassName, background = props.background, content = props.content, icon = props.icon, _d = props.showIcon, showIcon = _d === void 0 ? true : _d, _e = props.type, type = _e === void 0 ? 'success' : _e, closable = props.closable, closeElement = props.closeElement, onClose = props.onClose, afterClose = props.afterClose, _f = props.contentStyle, contentStyle = _f === void 0 ? {} : _f, _g = props.defaultLineClamp, _defaultLineClamp = _g === void 0 ? 3 : _g, _h = props.expandMode, expandMode = _h === void 0 ? true : _h, _j = props.hideExpandButtonOnInView, hideExpandButtonOnInView = _j === void 0 ? true : _j, _k = props.size, size = _k === void 0 ? 'default' : _k, rest = __rest(props, ["style", "className", "wrapperClassName", "background", "content", "icon", "showIcon", "type", "closable", "closeElement", "onClose", "afterClose", "contentStyle", "defaultLineClamp", "expandMode", "hideExpandButtonOnInView", "size"]);
    var t = useI18nCopy();
    var _l = __read(useState(true), 2), visible = _l[0], setVisible = _l[1];
    var globalPrefixCls = useConfigContext().prefixCls;
    var prefixCls = "".concat(globalPrefixCls, "-alert");
    var classNames = cx(prefixCls, "".concat(prefixCls, "-").concat(type), "".concat(prefixCls, "-size-").concat(size), (_a = {},
        _a["".concat(prefixCls, "-without-background")] = !background,
        _a), className);
    var _m = __read(useControlled(props, {
        valuePropName: 'expanded',
        onChangePropName: 'onExpandChange',
        defaultValuePropName: 'defaultExpanded',
    }), 2), expanded = _m[0], setExpanded = _m[1];
    var defaultLineClamp = expanded ? 'unset' : _defaultLineClamp;
    function renderIcon(type) {
        if (icon) {
            return icon;
        }
        switch (type) {
            case 'success':
                return React.createElement(ArcoIconWrapper, { Icon: IconSuccess });
            case 'warning':
                return React.createElement(ArcoIconWrapper, { Icon: IconWarning });
            case 'error':
                return React.createElement(ArcoIconWrapper, { Icon: IconError });
            case 'info':
                return React.createElement(ArcoIconWrapper, { Icon: IconInfoBlue });
            default:
                return null;
        }
    }
    function onHandleClose(e) {
        setVisible(false);
        onClose && onClose(e);
    }
    var tooltipProps = (_c = props.tooltipProps) !== null && _c !== void 0 ? _c : {};
    var contentRef = useRef();
    var _o = __read(useState(false), 2), contentOverflow = _o[0], setContentOverflow = _o[1];
    useLayoutEffect(function () {
        var dom = contentRef.current;
        if (!dom)
            return;
        setContentOverflow(dom.scrollHeight > dom.offsetHeight);
    }, [content]);
    var expandNode = useMemo(function () {
        var node = expanded ? (React.createElement(CollapseIcon, { containerProps: {
                onClick: function () {
                    setExpanded(false);
                },
            } })) : (React.createElement(MoreIcon, { containerProps: {
                onClick: function () {
                    setExpanded(true);
                },
            } }));
        if (!expandMode)
            return null;
        if (!hideExpandButtonOnInView)
            return node;
        if (!contentOverflow)
            return null;
        return node;
    }, [contentOverflow, expandMode, expanded, hideExpandButtonOnInView, setExpanded]);
    return (React.createElement(CSSTransition, { in: visible, timeout: 300, classNames: "zoomInTop", unmountOnExit: true, onExited: function () {
            afterClose && afterClose();
        } },
        React.createElement("div", __assign({ ref: ref, className: classNames, style: style, role: "alert", "data-tid": "m4b_alert" }, rest),
            React.createElement(Tooltip, __assign({}, tooltipProps, { content: typeof tooltipProps.content === 'string' ? (React.createElement("span", { className: "".concat(prefixCls, "-tooltip-content") }, tooltipProps.content)) : (tooltipProps.content) }),
                React.createElement("div", { className: cx("".concat(prefixCls, "-wrapper"), wrapperClassName) },
                    showIcon && React.createElement("div", { className: "".concat(prefixCls, "-icon-wrapper") }, renderIcon(type)),
                    content && (React.createElement("div", { className: cx("".concat(prefixCls, "-content-wrapper")) },
                        React.createElement("div", { className: cx("".concat(prefixCls, "-content"), (_b = {},
                                _b["".concat(prefixCls, "-content-with-tooltip")] = Boolean(props.tooltipProps),
                                _b)), style: __assign({ WebkitLineClamp: defaultLineClamp }, contentStyle), ref: contentRef },
                            Boolean(props.tooltipProps) && !background ? React.createElement("span", null, content) : content,
                            !background && closable && (React.createElement("button", { className: "".concat(prefixCls, "-close-btn"), onClick: onHandleClose }, closeElement || t.dismiss))),
                        expandNode)))),
            background && closable && (React.createElement("button", { className: "".concat(prefixCls, "-close-btn"), onClick: onHandleClose }, closeElement || React.createElement(IconClose, null))))));
};
var Alert = forwardRef(AlertRender);
Alert.displayName = 'Alert';
export default Alert;
